import {
  Alert,
  AlertDescription,
  AlertIcon,
  Box,
  Button,
  Divider,
  Flex,
  Heading,
  Stack,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  Wrap,
  WrapItem,
} from "@chakra-ui/react";
import { useTranslation } from "react-i18next";
import { NarrativeBasisDto, ProtocolBlockDto, ProtocolSectionDto } from "../../../autogen/bff-api";
import { FieldBlock, NoteBlock, ParagraphBlock, SubheadingBlock } from "../../../common/report/ReportTextBlocks";
import { NarrativeField } from "./NarrativeField";

const FieldRowBlock = ({ block }: { block: ProtocolBlockDto }) => (
  <Wrap spacingX="7" spacingY="1" py="1">
    {(block.fields ?? []).map((field, index) => (
      <WrapItem key={index}>
        <Text fontSize="sm" color="smMuted" mr="2">
          {field.label}
        </Text>
        <Text fontSize="sm" color="smText">
          {field.value || "–"}
        </Text>
      </WrapItem>
    ))}
  </Wrap>
);

const RankBadge = ({ value, isTop }: { value: string; isTop: boolean }) => (
  <Flex
    align="center"
    justify="center"
    boxSize="6"
    rounded="full"
    bg={isTop ? "smPrimary" : "smProtocolRowAltBg"}
    color={isTop ? "white" : "smMuted"}
    fontSize="xs"
    fontWeight="600"
  >
    {value}
  </Flex>
);

const IDENTITY_WIDTH_PX = 220;
const RANK_WIDTH_PX = 32;

const TableBlock = ({ block }: { block: ProtocolBlockDto }) => {
  const columns = block.columns ?? [];
  const rows = block.rows ?? [];
  const numericColumns = new Set(block.numericColumns ?? []);
  const emphasizeFirstRow = block.emphasizeFirstRow ?? false;

  const lastIndex = Math.max(columns.length - 1, 0);
  const edgePadding = (index: number) => ({
    pl: index === 0 ? "0" : "8px",
    pr: index === lastIndex ? "0" : "8px",
  });
  const identityColumn = columns.findIndex((_, index) => index !== block.rankColumn);
  const identityWidth = block.rankColumn == null ? IDENTITY_WIDTH_PX : IDENTITY_WIDTH_PX - RANK_WIDTH_PX;
  const columnWidth = (index: number) => {
    if (index === block.rankColumn) return `${RANK_WIDTH_PX}px`;
    if (index === identityColumn) return `${identityWidth}px`;
    return undefined;
  };

  const headerCellProps = {
    color: "smMuted",
    fontSize: "11px",
    textTransform: "uppercase" as const,
    letterSpacing: "0.03em",
    fontWeight: "600",
    borderBottom: "1.5px solid",
    borderColor: "smBorder",
    pt: "4px",
    pb: "6px",
  };

  return (
    <Box overflowX="auto" py="1">
      <Table variant="unstyled" width="100%" sx={{ tableLayout: "fixed" }}>
        <Thead>
          <Tr>
            {columns.map((col, colIndex) => (
              <Th
                key={col}
                {...headerCellProps}
                {...edgePadding(colIndex)}
                width={columnWidth(colIndex)}
                textAlign={numericColumns.has(colIndex) ? "right" : "left"}
              >
                {col}
              </Th>
            ))}
          </Tr>
        </Thead>
        <Tbody>
          {rows.length === 0 && block.emptyText ? (
            <Tr>
              <Td
                colSpan={Math.max(columns.length, 1)}
                textAlign="center"
                fontStyle="italic"
                color="smMuted"
                fontSize="sm"
                py="3"
                px="0"
              >
                {block.emptyText}
              </Td>
            </Tr>
          ) : (
            rows.map((row, rowIndex) => {
              const isWinner = emphasizeFirstRow && rowIndex === 0;
              const isLastRow = rowIndex === rows.length - 1;
              return (
                <Tr key={rowIndex}>
                  {row.map((cell, cellIndex) => (
                    <Td
                      key={cellIndex}
                      color="smText"
                      fontWeight={isWinner ? "600" : undefined}
                      borderBottom={isLastRow ? undefined : "1px solid"}
                      borderColor="smBorder"
                      {...edgePadding(cellIndex)}
                      py="9px"
                      textAlign={numericColumns.has(cellIndex) ? "right" : "left"}
                    >
                      {cellIndex === block.rankColumn ? (
                        <RankBadge value={cell ?? ""} isTop={isWinner} />
                      ) : (
                        <Text fontSize="sm" whiteSpace="pre-line">{cell || "–"}</Text>
                      )}
                    </Td>
                  ))}
                </Tr>
              );
            })
          )}
        </Tbody>
      </Table>
    </Box>
  );
};

interface Props {
  section: ProtocolSectionDto;
  onSaveNarrative: (editableKey: string, value: string, writtenAgainst?: NarrativeBasisDto | null) => void;
  isReadOnly?: boolean;
}

const InternalSectionNote = () => {
  const { t } = useTranslation();
  return <NoteBlock block={{ value: t("Internal note. Left out when the protocol is handed out.") }} />;
};

export const ProtocolSectionView = ({ section, onSaveNarrative, isReadOnly = false }: Props) => {
  const { t } = useTranslation();
  return (
    <Box bg="smBackground" rounded="xl" border="1px solid" borderColor="smBorder" p={{ base: "4", md: "6" }}>
      <Heading as="h2" size="sm" color="smText" mb="1">
        {section.number}. {section.heading}
      </Heading>
      <Divider mb="3" borderColor="smBorder" />
      {section.internal && <InternalSectionNote />}
      <Stack spacing="3">
        {section.blocks.map((block, index) => {
          if (block.kind === "paragraph" && block.editableKey) {
            return (
              <Box key={index} py="1">
                {block.label && (
                  <Text fontSize="sm" color="smMuted" mb="1">
                    {block.label}
                  </Text>
                )}
                {block.draftWarning && (
                  <Alert
                    status="warning"
                    rounded="md"
                    fontSize="sm"
                    mb="2"
                    alignItems="center"
                    flexWrap={{ base: "wrap", md: "nowrap" }}
                    data-testid="draft-warning"
                  >
                    <AlertIcon />
                    <AlertDescription flex="1">{block.draftWarning}</AlertDescription>
                    {!isReadOnly && (
                      <Button
                        size="sm"
                        variant="outline"
                        colorScheme="teal"
                        ml={{ base: "0", md: "3" }}
                        mt={{ base: "2", md: "0" }}
                        w={{ base: "full", md: "auto" }}
                        onClick={() => onSaveNarrative(block.editableKey as string, block.value ?? "", block.writtenAgainst)}
                      >
                        {t("The text is still correct")}
                      </Button>
                    )}
                  </Alert>
                )}
                <NarrativeField
                  initialValue={block.value ?? ""}
                  isDisabled={isReadOnly}
                  onSave={(value) => onSaveNarrative(block.editableKey as string, value, block.writtenAgainst)}
                />
              </Box>
            );
          }
          if (block.kind === "paragraph") return <ParagraphBlock key={index} block={block} />;
          if (block.kind === "note") return <NoteBlock key={index} block={block} />;
          if (block.kind === "subheading") return <SubheadingBlock key={index} block={block} />;
          if (block.kind === "table") return <TableBlock key={index} block={block} />;
          if (block.kind === "divider") {
            return (
              <Box key={index} py="2.5">
                <Divider borderColor="smBorder" />
              </Box>
            );
          }
          if (block.kind === "fieldRow") return <FieldRowBlock key={index} block={block} />;
          return <FieldBlock key={index} block={block} />;
        })}
      </Stack>
    </Box>
  );
};
